// Инициализация мониторинга (@sentry/react).
//
// Вызывается один раз из bootstrap/main.tsx. Без DSN — no-op: локальная разработка
// и офлайн-устройства не должны падать из-за выключенного мониторинга.
//
// DSN приходит из сборки (`VITE_SENTRY_DSN`): в CI — переменная репозитория
// `APP_SENTRY_DSN`, в Docker-образе — build-arg из `.env` сервера (T-050). Принимает
// Hawk (формат Sentry). DSN клиента публичен по построению — он лежит в бандле.
//
// Квота Hawk — 1 000 событий в месяц на сервер и приложение вместе (T-053). Одна ошибка
// рендера на десятке планшетов при каждом открытии экрана съела бы её за часы, и трекер
// замолчал бы до конца месяца, не сказав об этом. Поэтому перед отправкой — предохранитель
// (`admitEvent`), а сетевые ошибки не отправляются вовсе: офлайн на кухне — норма.

import * as Sentry from '@sentry/react';

import { env } from './env';

/** Событий за один запуск приложения — больше не нужно, чтобы понять, что сломалось. */
export const MAX_EVENTS_PER_LAUNCH = 20;
/** Событие одного вида — не чаще раза в это окно, мс. */
export const KIND_WINDOW_MS = 60 * 60 * 1000;
/** Конвертов в офлайн-очереди. Неделя без сети не должна выгрузиться сотней событий разом. */
const OFFLINE_QUEUE_SIZE = 30;
const KIND_STORAGE_KEY = 'restomarker.monitoring.kinds';

// Сетевые отказы браузера и WebView — то, что `fetch` бросает без сети. Только
// регулярки с якорями: строку SDK сравнивает как ПОДСТРОКУ, и `'Failed to fetch'`
// заглушил бы `Failed to fetch dynamically imported module` — битый бандл после
// обновления по воздуху, ровно то, что трекер обязан показать. Своя `NetworkError`
// приложения (`shared/api/httpClient`) отсеивается в `admitEvent` по имени.
export const NETWORK_ERRORS = [
  /^(TypeError: )?Failed to fetch( \(.*\))?$/,
  /^(TypeError: )?NetworkError when attempting to fetch resource\.?$/,
  /^(TypeError: )?Load failed$/,
  /^(TypeError: )?The network connection was lost\.?$/,
  /^(TypeError: )?The Internet connection appears to be offline\.?$/,
  /^(AbortError: )?The (operation|user) aborted a request\.?$/,
  /^(AbortError: )?The operation was aborted\.?$/,
];

let sentThisLaunch = 0;

/** Сбросить счётчик запуска — только для тестов. */
export function resetMonitoringState(): void {
  sentThisLaunch = 0;
}

function eventKind(event: Sentry.ErrorEvent): string {
  const exception = event.exception?.values?.at(-1);
  if (!exception) {
    return `msg:${event.message ?? ''}`;
  }
  // Верхний кадр — где ошибка возникла. Текст в вид входит: на клиенте он и есть
  // главное отличие (`Cannot read properties of undefined (reading 'name')`).
  const top = exception.stacktrace?.frames?.at(-1);
  const where = top ? `${top.filename ?? ''}:${top.lineno ?? ''}` : '';
  return `exc:${exception.type ?? ''}:${exception.value ?? ''}@${where}`;
}

function readKinds(): Record<string, number> {
  try {
    const raw = localStorage.getItem(KIND_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeKinds(kinds: Record<string, number>, now: number): void {
  try {
    // Старые виды выбрасываются: хранилище не растёт с каждым новым багом.
    const fresh = Object.fromEntries(
      Object.entries(kinds).filter(([, at]) => now - at < KIND_WINDOW_MS),
    );
    localStorage.setItem(KIND_STORAGE_KEY, JSON.stringify(fresh));
  } catch {
    // Без хранилища (приватный режим, квота) остаётся предел на запуск — этого хватает.
  }
}

/**
 * Предохранитель перед отправкой: `null` — событие не уходит.
 *
 * Работает в момент захвата, до офлайн-очереди: очередь копит уже прореженное.
 * Счёт вида — в `localStorage`, чтобы перезапуск приложения не обнулял окно: падение
 * при старте иначе давало бы событие на каждый запуск.
 */
export function admitEvent(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
  now: number = Date.now(),
): Sentry.ErrorEvent | null {
  const original = hint.originalException;
  if (original instanceof Error && original.name === 'NetworkError') {
    return null;
  }
  if (sentThisLaunch >= MAX_EVENTS_PER_LAUNCH) {
    return null;
  }
  const kinds = readKinds();
  const kind = eventKind(event);
  const last = kinds[kind];
  if (last !== undefined && now - last < KIND_WINDOW_MS) {
    return null;
  }
  kinds[kind] = now;
  writeKinds(kinds, now);
  sentThisLaunch += 1;
  return event;
}

export function initMonitoring(): void {
  if (!env.sentryDsn) {
    return;
  }

  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.mode,
    // Та же версия, что в `X-Client-Version` и в имени OTA-бандла: ошибку видно
    // в привязке к выпуску, который её принёс.
    release: __APP_VERSION__,
    // Очередь в IndexedDB вместо отправки «сразу или никогда». Без сети кухня работает
    // штатно (offline-first.md), и ошибка, случившаяся офлайн, иначе терялась бы ровно
    // тогда, когда её важнее всего узнать. Уходит, когда связь вернулась.
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    // Клиент SDK разворачивает `transportOptions` в параметры транспорта, и офлайн-транспорт
    // читает оттуда `maxQueueSize`. Тип `transportOptions` про офлайн-параметры не знает —
    // отсюда приведение.
    transportOptions: {
      maxQueueSize: OFFLINE_QUEUE_SIZE,
    } as Sentry.BrowserOptions['transportOptions'],
    ignoreErrors: NETWORK_ERRORS,
    beforeSend: (event, hint) => admitEvent(event, hint),
    // Персональные данные сотрудников в трейсы не уезжают (app.md §12).
    sendDefaultPii: false,
    // Трассировку производительности включим осознанно и отдельно: на кухонных
    // планшетах трафик дорог, а офлайн — норма, а не инцидент.
    tracesSampleRate: 0,
  });
}

/**
 * Сообщить о перехваченной ошибке. Без DSN — no-op, как и сама инициализация.
 *
 * Нужна там, где ошибку ловят ради понятного экрана (T-044: сбой записи печати на
 * устройстве): необработанный reject до мониторинга доходил сам, а перехваченный
 * без этого вызова исчез бы из него молча.
 */
export function reportError(error: unknown): void {
  if (!env.sentryDsn) {
    return;
  }
  Sentry.captureException(error);
}
